import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import {
  getOperationalProductsByClient,
  getSessionUserNoCache,
} from '../../services/operationalContextService'
import { getActiveLayout, parseLayoutConfig } from '../../utils/layoutUtils'
import LayoutPreview from '../../components/preview/LayoutPreview'
import {
  AdminHero,
  AdminSectionCard,
} from '../../components/settings/AdminConsoleShell'

const ProductsLayoutPreview = () => {
  const { layoutId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const selectedProductFromState = location?.state?.selectedProduct || null

  const [layout, setLayout] = useState(null)
  const [products, setProducts] = useState([])
  const [selectedProductIds, setSelectedProductIds] = useState([])
  const [loading, setLoading] = useState(false)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  const layoutConfig = useMemo(() => parseLayoutConfig(layout?.config), [layout])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      try {
        const user = await getSessionUserNoCache()
        const productsPromise = user?.clientId
          ? getOperationalProductsByClient(user.clientId)
          : Promise.resolve([])
        const selectedProductId = selectedProductFromState?.id

        let resolvedLayout = null

        if (selectedProductId) {
          const layoutsData = await getLayoutsByProduct(selectedProductId)
          const payload = layoutsData?.data ?? layoutsData
          const layouts = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.layouts)
              ? payload.layouts
              : payload
                ? [payload]
                : []

          resolvedLayout =
            layouts.find((candidate) => String(candidate?.id) === String(layoutId)) ||
            getActiveLayout(layouts)
        } else {
          const layoutRes = await api.get(`/layouts/${layoutId}`)
          resolvedLayout = layoutRes?.data || null
        }

        const operationalProducts = await productsPromise

        setLayout(resolvedLayout)
        setProducts(
          Array.isArray(operationalProducts) ? operationalProducts : []
        )
      } catch (err) {
        console.error('[ProductsLayoutPreview] Error:', err)

        const status = err?.response?.status
        const msg =
          status === 403
            ? 'You do not have permissions to view this layout (RBAC).'
            : 'Failed to load layout or products.'

        setSnackbar({
          open: true,
          message: msg,
          severity: 'error',
        })

        setLayout(null)
        setProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [layoutId, selectedProductFromState])

  const toggleProduct = (id) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleAssignLayout = async () => {
    try {
      await api.patch(`/layouts/${layoutId}/apply`, {
        productIds: selectedProductIds,
      })

      setSnackbar({
        open: true,
        message: 'Layout assigned successfully.',
        severity: 'success',
      })
    } catch (err) {
      console.error('[ProductsLayoutPreview] assign error:', err)
      setSnackbar({
        open: true,
        message: 'Failed to assign layout.',
        severity: 'error',
      })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      <AdminHero
        eyebrow="Preview and validation"
        title="Layout Preview"
        description="Vista de confianza para validar composición visual del layout y confirmar a qué productos se aplicará sin cambiar el flujo actual."
        stats={[
          { label: 'Layout ID', value: layoutId || 'N/A' },
          { label: 'Campos', value: layoutConfig.length },
          { label: 'Productos marcados', value: selectedProductIds.length },
        ]}
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button variant="contained" onClick={() => navigate('/settings/controlpanel/layout-manager')}>
              Volver al manager
            </Button>
            <Button variant="outlined" startIcon={<AssignmentTurnedInIcon />} onClick={handleAssignLayout} disabled={selectedProductIds.length === 0}>
              Asignar layout
            </Button>
          </Stack>
        }
      />

      {loading && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      )}

      {!loading && !layout && (
        <Alert severity="warning">Unable to load the layout.</Alert>
      )}

      {!loading && layout && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} xl={8}>
            <AdminSectionCard
              title="Visual preview"
              subtitle="Representación del layout usando el renderer actual para validar jerarquía, spacing y distribución de campos."
              actions={<VisibilityIcon color="action" />}
            >
              {layoutConfig.length === 0 ? (
                <Alert severity="info">
                  This layout has no fields in config, or config format is invalid.
                </Alert>
              ) : (
                <LayoutPreview layoutConfig={layoutConfig} values={{}} />
              )}
            </AdminSectionCard>
          </Grid>

          <Grid item xs={12} xl={4}>
            <Stack spacing={2.5}>
              <AdminSectionCard
                title="Resumen del layout"
                subtitle="Información operativa para identificar rápidamente el artefacto activo y su contexto."
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {layout.name || 'Layout'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Layout ID: {layoutId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fields: {layoutConfig.length}
                  </Typography>
                  {selectedProductFromState && (
                    <Typography variant="body2" color="text.secondary">
                      Context product: {selectedProductFromState?.name_product} (ID: {selectedProductFromState?.id})
                    </Typography>
                  )}
                </Stack>
              </AdminSectionCard>

              <AdminSectionCard
                title="Aplicar a productos"
                subtitle="Selecciona productos destino para aplicar el layout actual con el endpoint existente."
                actions={
                  <Button
                    variant="contained"
                    disabled={selectedProductIds.length === 0}
                    onClick={handleAssignLayout}
                    startIcon={<AssignmentTurnedInIcon />}
                  >
                    Assign layout
                  </Button>
                }
              >
                {products.length === 0 ? (
                  <Alert severity="info">No products available.</Alert>
                ) : (
                  <Grid container spacing={1}>
                    {products.map((p) => (
                      <Grid item xs={12} key={p.id}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: selectedProductIds.includes(p.id) ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedProductIds.includes(p.id)}
                                onChange={() => toggleProduct(p.id)}
                              />
                            }
                            label={p.name_product}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </AdminSectionCard>
            </Stack>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProductsLayoutPreview
